<template>
    <button
        class="select-trigger border relative w-full text-left pt-1.5 pb-1.5 min-h-[36px] focus-visible:ring-1 focus-visible:ring-blue-500 focus-visible:border-blue-500 rounded select-none transition-colors duration-150"
        :aria-describedby="ariaDescribedby"
        :aria-label="ariaLabel"
        :class="[
            fontSizeClassName,
            effectivePaddingClassName,
            backgroundClassName,
            !disabled && !highlighted && !boldHighlighted
                ? hasError
                    ? 'hover:border-red-500 hover:border-opacity-100 focus-within:border-red-500 focus-within:border-opacity-100'
                    : opened
                    ? 'hover:border-blue-500 hover:border-opacity-100 focus-within:border-blue-500 focus-within:border-opacity-100'
                    : hoverBorderClassName
                : '',
            disabled ? 'cursor-not-allowed' : 'cursor-pointer',
            {
                'pr-9': !state.isTaggable && [paddingClassName].flat().join(' ').includes('px-3'),
                'pr-8': !state.isTaggable && [paddingClassName].flat().join(' ').includes('px-2.5'),
                'bg-transparent border-transparent': invisible && !opened,
                'bg-gray-100': disabled && !invisible,
                'border-blue-500': !disabled && !invisible && highlighted && hasSelectedValue,
                'border-gray-200': !disabled && !invisible && highlighted && !hasSelectedValue,
                'bg-blue-500 text-white border-blue-500':
                    !disabled && !invisible && boldHighlighted && hasSelectedValue,
                'border-blue-500 ring-1 ring-blue-500 bg-white': opened,
                'border-red-500': hasError,
                'ring-1 ring-red-400': hasError && opened,
            },
        ]"
        data-testid="select-trigger"
        :disabled="disabled"
        type="button"
    >
        <span class="flex items-center space-x-2">
            <span class="shrink-0 flex">
                <slot name="triggerIcon" />
            </span>
            <template v-if="!isEmpty">
                <span class="flex grow flex-wrap gap-x-2 gap-y-1 pr-9" v-if="state.isTaggable">
                    <span
                        class="inline-flex items-center rounded bg-blue-500 text-white"
                        :class="oldFontScale ? 'text-sm' : 'text-xs'"
                        data-testid="select-tag"
                        :key="String(selectedOption.value)"
                        v-for="selectedOption in state.selectedOptions"
                    >
                        <span class="px-2 py-0.5" v-text="selectedOption.label" />
                        <button
                            class="shrink-0 inline-flex items-center justify-center rounded-full text-white text-opacity-75 pr-2 transition-colors duration-150 hover:text-opacity-100 focus:outline-none"
                            data-testid="select-tag-remove"
                            type="button"
                            @click="events.onDelete(selectedOption.value)"
                        >
                            <close class="h-2 w-2" />
                        </button>
                    </span>
                </span>
                <span
                    class="block grow truncate"
                    data-testid="select-label"
                    v-text="label"
                    v-else
                />
            </template>
            <span
                class="block grow truncate text-gray-500"
                data-testid="select-placeholder"
                v-text="state.placeholder"
                v-else
            />
            <button
                class="absolute inset-y-0 right-0 flex items-center justify-center w-9 text-gray-400 transition-colors duration-150 hover:text-gray-500 focus:outline-none"
                data-testid="select-clear"
                type="button"
                @click.stop.prevent="events.onClear()"
                v-if="shouldShowClear"
            >
                <close class="h-3 w-3" />
            </button>
            <span
                class="absolute inset-y-0 right-0 flex items-center justify-center w-9 pointer-events-none"
                v-else
            >
                <svg
                    class="h-3 w-3 text-gray-400 animate-spin"
                    data-testid="select-loading"
                    fill="none"
                    viewBox="0 0 24 24"
                    v-if="loading"
                >
                    <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                    />
                    <path
                        class="opacity-75"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        fill="currentColor"
                    />
                </svg>
                <interface-page-controller-scroll-up-down class="h-3 w-3 text-gray-500" v-else />
            </span>
        </span>
    </button>
</template>

<script lang="ts" setup>
    import { computed, inject } from 'vue';
    import { useI18n } from '@/hooks/useI18n.ts';
    import InterfacePageControllerScrollUpDown from '@/components/activix-ui/ActivixSelect/assets/interface-page-controller-scroll-up-down.svg';
    import Close from '@/components/activix-ui/ActivixSelect/assets/close.svg';
    import type {
        SelectState,
        SelectEvents,
        SelectColor,
    } from '@/components/activix-ui/ActivixSelect/types.ts';

    const { t } = useI18n();

    const props = withDefaults(
        defineProps<{
            ariaDescribedby?: string;
            ariaLabel?: string;
            color?: SelectColor;
            clearable?: boolean;
            disabled?: boolean;
            hasError?: boolean;
            highlighted?: boolean;
            boldHighlighted?: boolean;
            invisible?: boolean;
            limitSelectedVisible?: number;
            limitText?: (count: number) => string;
            loading?: boolean;
            opened?: boolean;
            paddingClassName: string | unknown[];
        }>(),
        {
            ariaDescribedby: undefined,
            ariaLabel: undefined,
            color: 'gray',
            clearable: false,
            disabled: false,
            hasError: false,
            highlighted: false,
            boldHighlighted: false,
            invisible: false,
            limitSelectedVisible: undefined,
            limitText: undefined,
            loading: false,
            opened: false,
        }
    );

    const state = inject<SelectState>('state')!;
    const events = inject<SelectEvents>('events')!;
    const oldFontScale = inject<boolean>('oldFontScale', true);

    const label = computed(() => {
        const selectedCount = state.selectedOptions.length;

        if (selectedCount > 1 && state.withCount && !state.isTaggable) {
            // Use limitSelectedVisible if provided
            if (
                props.limitSelectedVisible !== undefined &&
                selectedCount > props.limitSelectedVisible
            ) {
                if (props.limitText) {
                    return props.limitText(selectedCount);
                }
                return `${selectedCount} ${t('clientCard.select.selected')}`;
            }
            return `${selectedCount} ${t('clientCard.select.selected')}`;
        }

        // For single selection or when not using count display
        const joinedLabels = state.selectedOptions
            .map((selectedOption) => selectedOption.label)
            .join(', ');

        // Check if we should truncate based on limitSelectedVisible
        if (
            props.limitSelectedVisible !== undefined &&
            selectedCount > props.limitSelectedVisible
        ) {
            if (props.limitText) {
                return props.limitText(selectedCount);
            }
            return `${selectedCount} ${t('clientCard.select.selected')}`;
        }

        return joinedLabels;
    });

    // An option with a label is considered non-empty even if its value is null/undefined/''.
    // This ensures nullable placeholder options like { value: null, label: 'All divisions' }
    // display their label text instead of falling back to the placeholder.
    const isEmpty = computed(() => {
        return (
            !state.selectedOptions.length ||
            state.selectedOptions.every((selectedOption) => {
                return (
                    (selectedOption.value === null ||
                        selectedOption.value === undefined ||
                        selectedOption.value === '') &&
                    !selectedOption.label
                );
            })
        );
    });

    const fontSizeClassName = computed(() => {
        return oldFontScale ? 'text-base' : 'text-sm';
    });

    const effectivePaddingClassName = computed(() => {
        if (!state.isTaggable) {
            return props.paddingClassName;
        }

        const normalizedPadding = [props.paddingClassName].flat().join(' ');
        return normalizedPadding.includes('py-1') ? 'py-1 px-0' : 'py-2 px-0';
    });

    const hasSelectedValue = computed(() => {
        return state.selectedOptions.length > 0;
    });

    const isNoneOnlySelection = computed(() => {
        return (
            state.selectedOptions.length > 0 &&
            state.selectedOptions.every((selectedOption) => selectedOption.value === 'none')
        );
    });

    // True when all selected options have null/undefined/empty values (nothing meaningful to clear).
    const isNullOnlySelection = computed(() => {
        return (
            state.selectedOptions.length > 0 &&
            state.selectedOptions.every(
                (selectedOption) =>
                    selectedOption.value === null ||
                    selectedOption.value === undefined ||
                    selectedOption.value === ''
            )
        );
    });

    const shouldShowClear = computed(() => {
        return (
            props.clearable &&
            !props.disabled &&
            !state.isTaggable &&
            hasSelectedValue.value &&
            !isNoneOnlySelection.value &&
            !isNullOnlySelection.value &&
            !isEmpty.value
        );
    });

    /** True when the trigger should show its normal color-based background (not disabled, invisible, or highlighted) */
    const hasNormalBackground = computed(() => {
        return (
            !props.disabled &&
            !props.invisible &&
            !props.highlighted &&
            !props.boldHighlighted &&
            !props.opened
        );
    });

    /** Background class based on the color prop, only applied when hasNormalBackground is true */
    const backgroundClassName = computed(() => {
        if (!hasNormalBackground.value) return '';

        switch (props.color) {
            case 'white':
                return 'bg-white border-gray-400';
            case 'gray':
                return 'bg-gray-100 border-gray-400';
            case 'legacyGray':
                return 'bg-gray-200 border-gray-400';
            case 'transparent':
                return 'bg-transparent border-transparent';
            case 'outline':
                return 'bg-white border-gray-400 border-2';
            default:
                return 'bg-white border-gray-400';
        }
    });

    /** Hover/focus border classes based on the `color` prop so hover shows a concrete color */
    const hoverBorderClassName = computed(() => {
        switch (props.color) {
            case 'white':
            case 'gray':
            case 'legacyGray':
            case 'outline':
                return 'hover:border-gray-400 hover:border-opacity-100 focus-within:border-gray-400 focus-within:border-opacity-100';
            case 'transparent':
                return 'hover:border-gray-200 hover:border-opacity-100 focus-within:border-gray-200 focus-within:border-opacity-100';
            default:
                return 'hover:border-gray-400 hover:border-opacity-100 focus-within:border-gray-400 focus-within:border-opacity-100';
        }
    });
</script>
